import React, { useMemo, useRef, useCallback, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '~/util'
import './multi-switch.css'
import type { MultiSwitchOptionProps } from './MultiSwitchOption'

interface MultiSwitchProps<T = string> {
  selection: T
  onChange: (value: T) => void
  children?: React.ReactElement<MultiSwitchOptionProps<T>>[]
}

function MultiSwitch<T = string>({ selection, onChange, children }: MultiSwitchProps<T>) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const uniqueId = useId()

  const newPosition = useMemo(() => {
    const option = React.Children.toArray(children).find(
      (child): child is React.ReactElement<MultiSwitchOptionProps<T>> =>
        React.isValidElement(child) && child.props.value === selection
    )
    return option?.props.position
  }, [selection, children])

  const options = React.Children.toArray(children) as React.ReactElement<
    MultiSwitchOptionProps<T>
  >[]
  const currentIndex = options.findIndex((option) => option.props.value === selection)

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          if (currentIndex > 0) {
            const prevOption = options[currentIndex - 1]
            if (prevOption) {
              onChange(prevOption.props.value)
            }
          }
          break
        case 'ArrowRight':
          event.preventDefault()
          if (currentIndex < options.length - 1) {
            const nextOption = options[currentIndex + 1]
            if (nextOption) {
              onChange(nextOption.props.value)
            }
          }
          break
        case ' ':
        case 'Enter':
          event.preventDefault()
          if (options.length === 2) {
            const nextIndex = currentIndex === 0 ? 1 : 0
            const toggleOption = options[nextIndex]
            if (toggleOption) {
              onChange(toggleOption.props.value)
            }
          }
          break
      }
    },
    [currentIndex, options, onChange]
  )

  return (
    <div
      ref={containerRef}
      className={cn(
        options.length === 3 ? 'triple' : 'double',
        'switch-container light bg-slider/75'
      )}
      tabIndex={0}
      role="radiogroup"
      aria-label={t('legend.switchOptions')}
      onKeyDown={handleKeyDown}
    >
      <div className={`switch ${newPosition}-position`} />
      {React.Children.map(children, (option) =>
        React.cloneElement(option as React.ReactElement<MultiSwitchOptionProps<T>>, {
          getSwitchAnimation: (_: string | number) => {
            const valueOfSelection = (option as React.ReactElement<MultiSwitchOptionProps<T>>).props
              .value
            onChange(valueOfSelection)
          },
          switchPosition: newPosition as string,
          groupName: uniqueId,
        })
      )}
    </div>
  )
}

export default MultiSwitch
